import React from 'react'
import { NotificationUI } from '../feedback/NotificationUI'
import { DashboardHeader } from './DashboardHeader'
import { cn } from '@/core'

export interface PageContainerProps {
  children: React.ReactNode
  className?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  /** Show global notification toast */
  showNotifications?: boolean
  /** Optional header component */
  header?: React.ReactNode
  containerClassName?: string
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
  '2xl': 'max-w-7xl',
  full: 'w-full',
}

/**
 * PageContainer Component
 *
 * Main layout wrapper for pages with structure:
 * - NotificationUI (fixed, global)
 * - Header (optional)
 * - Children (page content)
 */
export function PageContainer({
  children,
  className,
  maxWidth = '2xl',
  showNotifications = true,
  header,
  containerClassName,
}: PageContainerProps) {
  return (
    <div
      className={cn(
        'min-h-screen bg-linear-to-br from-gray-50 via-white to-gray-50 flex flex-col ',
        containerClassName,
      )}
    >
      <DashboardHeader className="" />
      {/* Global Notification UI */}
      {showNotifications && <NotificationUI />}

      <div
        className={cn(
          'mx-auto px-4 py-8 sm:px-6 lg:px-6 space-y-3 flex-1 max-h-full w-full ',
          maxWidthClasses[maxWidth],
          className,
        )}
      >
        {/* Optional Header */}
        {header}

        {/* Page Content */}
        {children}
      </div>
    </div>
  )
}

export interface PageHeaderProps {
  title: string
  subtitle?: string
  action?: React.ReactNode
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  action,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        'mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4',
        className,
      )}
    >
      <div className="flex-1">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          {title}
        </h1>
        {subtitle && (
          <p className="text-gray-600 text-sm sm:text-base">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export interface PageSectionProps {
  title?: string
  description?: string
  children: React.ReactNode
  className?: string
}

export function PageSection({
  title,
  description,
  children,
  className,
}: PageSectionProps) {
  return (
    <section className={cn('mb-8', className)}>
      {(title || description) && (
        <div className="mb-6">
          {title && (
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{title}</h2>
          )}
          {description && (
            <p className="text-gray-600 text-sm">{description}</p>
          )}
        </div>
      )}
      {children}
    </section>
  )
}
